import type { TemplateResult } from 'lit';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { EntityContent } from 'src/content/entity-content';
import '../../library/editors';
import '../../library/fields';
import '../../library/fields/d-view-info';
import '../../library/lists/d-list-section';
import '../../library/lists/d-list-section-attachment';
import '../../library/lists/d-list-section-task-events';
import './d-smart-task-info';
import { t } from 'src/i18n';
import { isEmptyOrInvalidString, uuid } from 'src/utilities/text';
import type { UpdateSectionItem } from '../update-section-item';
import type { CreateEventOccurrenceInput } from '../../layout/parts/create-event-occurrence-input';
import type { CreateEntityInput } from '../../layout/parts/create-entity-input';
import type { ListDefinition } from '../list-definition';
import type { TaskView } from './task-view';
import type { TaskViewEditItem } from './task-view-edit-item';
import type { SectionField } from '../../library/components/section-field';
import type { SelectTagOption } from '../../library/editors/elements/select-tag-option';
import { pageIds } from 'src/store/selectors/pages/pageIds';

/**
 *
 *
 *
 *
 * Displays the task view. The task view shows the routines.p+
 *
 *
 */
@customElement('d-task-view')
export class DTaskView extends EntityContent<TaskView, TaskViewEditItem> {
  static readonly styles = EntityContent.styles;
  assetSelectionOptions = [
    { value: 'COMMON', text: t('tasks.commonTasks') },
    { value: 'INDIVIDUAL', text: t('tasks.individualTasks') },
  ];
  @property({ type: Object })
  organization: any = {};
  @property({ type: Boolean })
  singleUserVersion = false;
  @property({ type: String })
  currentEmployeeUuid = '';
  @property({ type: String })
  currentEmployeeShortName = '';
  @property({ type: Array })
  employeesNotDeleted: any[] = [];
  @property({ type: Array })
  employeesShortNames: any[] = [];
  @property({ type: String })
  entityType = 'tasks';
  @property({ type: String })
  parentEntityType = 'functions';
  @property({ type: String })
  functions = '';
  @property({ type: Array })
  events: any[] = [];
  @property({ type: Object })
  parent = {};
  @property({ type: String })
  conditionDescription = '';
  @property({ type: String })
  defaultFunctionForAssetTask = '';
  /*
    viewHref2(entityId, organizationId, pageId, entityType) {
      return (
        '/account/' + organizationId + '/' + pageId + '/functions/' + this.entityView.parentEntityId + '/tasks/' + this.entityId
      );
    }

     */
  @property({ type: Array })
  relatedEventInstancesLevel3: any[] = [];
  @property({ type: Array })
  relatedEventInstancesLevel4: any[] = [];
  @property({ type: Array })
  relatedEventInstancesLevel5: any[] = [];
  @property({ type: Array })
  startTasks: any[] = [];
  @property({ type: Array })
  pagesForSingleUserVersion: any[] = [];
  @property({ type: Array })
  pagesForSingleUserVersionEditable: number[] = [];
  @property({ type: Boolean })
  includeDetailsInAlert = true;
  @property({ type: String })
  alertMessage = '';

  protected get preventDelete(): boolean {
    return this.entityView.hasEvents;
  }

  renderViewMode() {
    return html`${this.renderFieldSection('tasks', this.viewFields(), this.entityView)}`;
  }

  renderRelatedRiskAssessments() {
    const assessments = this.entityView.relatedRiskAssessments;
    if (assessments.length > 0) {
      return html`
        <div class="related-entities-section risk-assessment-section">
          <div>
            ${assessments.length > 1 ? t('tasks.measureInRiskAssessments') : t('tasks.measureInRiskAssessment')}
            ${this.entityView.relatedRiskAssessments.map((r) => {
              return html` <a href="${r.href}">${r.name}</a> `;
            })}
          </div>
        </div>
      `;
    }
    return html``;
  }

  renderPreContent() {
    return this.renderRelatedRiskAssessments();
  }

  renderTaskEventsLists(): TemplateResult<1> {
    return html`
      <d-list-section-task-events
        .items=${this.entityView.taskEvents}
        .writeAccess="${this.entityView.currentUserHasWriteAccess}"
        .href=${this.entityView.href}
        .taskUuid=${this.entityView.uuid}
        .startTaskText="${this.entityView.startTaskText}"
        icon="events"
        .contentStickyTop=${this.contentStickyTop}
      >
      </d-list-section-task-events>
    `;
  }

  async initializeEditItem() {
    this.editItem = {
      functionUuid: this.entityView.parentEntityId,
      procedures: this.entityView.procedures,
      responsibleFunctionUuid: this.entityView.responsibleFunctionUuid,
      situation: this.entityView.situation,
      pages: this.entityView.pages,
      name: this.entityView.name,
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  renderEditItem(item: TaskViewEditItem): TemplateResult<1> {
    return html`${!this.entityView.hasEvents
        ? nothing
        : html`<d-section>
            <d-view-info field="tasks_hasRelatedEventsText"></d-view-info>
          </d-section>`}

      <d-section>
        <d-edit-text
          field="tasks_name"
          .autofocus=${!this.entityView.isConfirmedEntity}
          .selectOnFocus=${!this.entityView.isConfirmedEntity}
          placeholder="${t('tasks.requiredField')}"
          mark-if-empty
          .value=${item.name}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            this.editItem = { ...item, name: e.detail.value };
          }}
        ></d-edit-text>
      </d-section>

      ${this.singleUserVersion
        ? nothing
        : html`<d-section>
            <d-select-dropdown
              field="tasks_function"
              class="minWidth300"
              .options=${this.entityView.availableFunctions}
              .value=${item.functionUuid}
              @value-changed=${(e: CustomEvent<{ value: string }>) => {
                this.editItem = { ...item, functionUuid: e.detail.value };
              }}
            >
            </d-select-dropdown>
            ${this.entityView.featureStates.quality
              ? html` <d-select-dropdown
                  field="tasks_responsible"
                  class="minWidth300"
                  .options=${this.entityView.availableFunctionsForResponsible}
                  placeholder="${t('tasks.selectResponsibility')}"
                  .value=${item.responsibleFunctionUuid}
                  @value-changed=${(e: CustomEvent<{ value: string }>) => {
                    this.editItem = { ...item, responsibleFunctionUuid: e.detail.value };
                  }}
                >
                </d-select-dropdown>`
              : nothing}
          </d-section>`}
      ${!this.entityView.relatedRiskAssessments.length
        ? html`
            <d-section>
              <d-edit-textarea
                field="tasks_situation"
                .value=${item.situation}
                @value-changed=${(e: CustomEvent<{ value: string }>) => {
                  this.editItem = { ...item, situation: e.detail.value };
                }}
              >
              </d-edit-textarea>
            </d-section>
          `
        : nothing}

      <d-edit-html
        field="tasks_procedures"
        .showChecklist=${true}
        .value=${item.procedures}
        .docsForLinking=${this.entityView.docsForLinking}
        .contentStickyTop=${this.contentStickyTop}
        @value-changed=${(e: CustomEvent<{ value: string }>) => {
          this.editItem = { ...item, procedures: e.detail.value };
        }}
      ></d-edit-html> `;
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'assignedFunctionName',
        fieldForLabel: 'function',
        type: 'view-text',
        class: 'minWidth300',
        condition: !this.singleUserVersion,
      },
      {
        field: 'responsibleFunctionName',
        fieldForLabel: 'responsible',
        type: 'view-text',
        class: 'minWidth300',
        condition: !this.singleUserVersion && this.entityView.featureStates.quality,
      },
      {
        field: 'situation',
        type: 'view-text',
        class: 'fullWidth',
      },
      {
        type: 'view-smart-task-info',
        functionName: this.entityView.assignedFunctionName,
        text: this.entityView.smartTaskEventText,
        condition: !isEmptyOrInvalidString(this.entityView.smartTaskEventText),
      },
      {
        field: 'procedures',
        type: 'view-html',
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: '',
        type: 'custom',
        icon: '',
        items: '',
        render: () => this.renderTaskEventsLists(),
        // Tasks related to risk assessments are hypothetical and should not have events
        condition: this.entityView.relatedRiskAssessments.length === 0,
      },
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  protected renderEditSelectPages(item: any): TemplateResult<1> {
    if (
      (this.entityView.featureStates.core || this.entityView.featureStates.hse) &&
      this.entityView.availablePages &&
      this.entityView.availablePages.length
    ) {
      return html`
        ${this.singleUserVersion
          ? html`<d-section>
              <d-select-tag
                label="${t('content.showOnThemePage')}"
                not-deselectable
                .options=${this.entityView.availablePages}
                .value=${item.pages}
                @value-changed=${(e: CustomEvent<{ value: string[] }>) => {
                  this.editItem = { ...item, pages: e.detail.value };
                }}
              >
              </d-select-tag>
            </d-section>`
          : html`<d-section>
              <d-select-tag
                label="${t('tasks.showAlsoOnThemePage')}"
                .options=${this.availablePages(item.functionUuid, item.responsibleFunctionUuid)}
                .value=${item.pages}
                @value-changed=${(e: CustomEvent<{ value: string[] }>) => {
                  this.editItem = { ...item, pages: e.detail.value };
                }}
              >
              </d-select-tag>
            </d-section>`}
      `;
    }
    return html``;
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return this.entityView;
  }

  private async onAddEvent(e: CustomEvent) {
    e.preventDefault();
    e.stopPropagation();

    const id = uuid();
    const input: CreateEventOccurrenceInput = {
      entityType: 'eventOccurrences',
      entityUuid: id,
      targetUrl: this.entityView.href + '/eventOccurrences/' + id + '?edit',
      taskUuid: this.entityView.uuid,
    };

    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  private disableEmergencyPage(pages: SelectTagOption[]): SelectTagOption[] {
    return pages.map((p) => {
      if (p.value === pageIds.emergency + '' && !this.entityView.currentUserHasEmergencyAccess) {
        return { ...p, disabled: true };
      }
      return p;
    });
  }

  private availablePages(functionUuid: string, responsibleFunctionUuid: string): SelectTagOption[] {
    const set1 = this.entityView.disabledPagesPerFunction.get(functionUuid) ?? new Set<number>();
    const set2 = this.entityView.disabledPagesPerFunction.get(responsibleFunctionUuid) ?? new Set<number>();
    const disabledPages = new Set<number>([...set1, ...set2]);
    const pages = this.entityView.availablePages.map((x) => ({ ...x, disabled: disabledPages.has(Number(x.value)) }));
    return this.disableEmergencyPage(pages);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-task-view': DTaskView;
  }
}
